/* ==========================================================================
   BRASSLUXE MOROCCO — finish-morph.js (reusable ES module)
   Mini 3D brass faucet whose material morphs between finishes.
   Used by sections/finish-library.liquid AND the product page.

   Public API (exposed on window.BrassLuxeFinish):

     BrassLuxeFinish.mountScene(canvasEl, {
       color: '#C9A227',      // hex string
       roughness: 0.22,       // 0..1
       metalness: 1.0         // 0..1
     }) → Promise resolving to {
       setFinish({ color, roughness, metalness }),  // GSAP-tweened live morph
       dispose()                                    // full teardown
     }

   Internals: dynamic import(window.__BLX.threeUrl) lazily, LatheGeometry +
   primitives faucet, MeshPhysicalMaterial, 2 lights, slow idle rotation,
   pixelRatio ≤ 1.75, render loop pauses off-screen, proper disposal.
   ========================================================================== */
(function () {
  'use strict';

  var threePromise = null;
  var dynamicImport = new Function('url', 'return import(url)');

  function loadThree() {
    if (!threePromise) threePromise = dynamicImport(window.__BLX.threeUrl);
    return threePromise;
  }

  /* Procedural equirect environment so polished metal has something to
     reflect (core three only — no example modules). */
  function makeEnvironment(THREE, renderer) {
    var c = document.createElement('canvas');
    c.width = 128; c.height = 64;
    var ctx = c.getContext('2d');
    var g = ctx.createLinearGradient(0, 0, 0, 64);
    g.addColorStop(0, '#fff8e2');
    g.addColorStop(0.42, '#c8ad6d');
    g.addColorStop(0.58, '#4a3b28');
    g.addColorStop(1, '#171310');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 128, 64);
    ctx.fillStyle = 'rgba(255,255,255,0.95)';
    ctx.fillRect(14, 6, 26, 12);   // "window" highlight
    ctx.fillStyle = 'rgba(255,244,214,0.7)';
    ctx.fillRect(86, 10, 18, 8);   // secondary highlight
    var tex = new THREE.CanvasTexture(c);
    tex.mapping = THREE.EquirectangularReflectionMapping;
    tex.colorSpace = THREE.SRGBColorSpace;
    var pmrem = new THREE.PMREMGenerator(renderer);
    var env = pmrem.fromEquirectangular(tex).texture;
    tex.dispose();
    pmrem.dispose();
    return env;
  }

  /* Elegant bridge faucet: lathe-turned body, gooseneck spout,
     two cross handles. Returns a group centred near origin. */
  function buildFaucet(THREE, material) {
    var group = new THREE.Group();

    /* body column */
    var profile = [
      [0.001, 0.00], [0.30, 0.00], [0.33, 0.04], [0.26, 0.10],
      [0.17, 0.16], [0.14, 0.20], [0.14, 0.50], [0.17, 0.56],
      [0.13, 0.62], [0.13, 0.86], [0.10, 0.92], [0.001, 0.94]
    ].map(function (p) { return new THREE.Vector2(p[0], p[1]); });
    group.add(new THREE.Mesh(new THREE.LatheGeometry(profile, 48), material));

    /* gooseneck spout */
    var curve = new THREE.CatmullRomCurve3([
      new THREE.Vector3(0, 0.80, 0),
      new THREE.Vector3(0, 1.08, 0),
      new THREE.Vector3(0, 1.26, 0.16),
      new THREE.Vector3(0, 1.27, 0.40),
      new THREE.Vector3(0, 1.12, 0.50),
      new THREE.Vector3(0, 0.99, 0.50)
    ]);
    group.add(new THREE.Mesh(new THREE.TubeGeometry(curve, 48, 0.072, 24), material));
    var aerator = new THREE.Mesh(new THREE.CylinderGeometry(0.085, 0.078, 0.07, 24), material);
    aerator.position.set(0, 0.965, 0.5);
    group.add(aerator);

    /* bridge cross handles, one per side */
    [-1, 1].forEach(function (side) {
      var escutcheon = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.13, 0.05, 32), material);
      escutcheon.position.set(side * 0.34, 0.025, 0);
      group.add(escutcheon);

      var stem = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.055, 0.22, 20), material);
      stem.position.set(side * 0.34, 0.15, 0);
      group.add(stem);

      var hub = new THREE.Mesh(new THREE.SphereGeometry(0.055, 20, 16), material);
      hub.position.set(side * 0.34, 0.28, 0);
      group.add(hub);

      var bar = new THREE.Mesh(new THREE.CylinderGeometry(0.024, 0.024, 0.30, 14), material);
      bar.rotation.z = Math.PI / 2;
      bar.position.set(side * 0.34, 0.30, 0);
      group.add(bar);

      [-1, 1].forEach(function (end) {
        var knob = new THREE.Mesh(new THREE.SphereGeometry(0.038, 16, 12), material);
        knob.position.set(side * 0.34 + end * 0.15, 0.30, 0);
        group.add(knob);
      });
    });

    group.position.y = -0.55;
    return group;
  }

  window.BrassLuxeFinish = {
    mountScene: function (canvas, initial) {
      var opts = initial || {};
      var init = {
        color: opts.color || '#C9A227',
        roughness: typeof opts.roughness === 'number' ? opts.roughness : 0.22,
        metalness: typeof opts.metalness === 'number' ? opts.metalness : 1.0
      };

      return loadThree().then(function (THREE) {
        var renderer = new THREE.WebGLRenderer({
          canvas: canvas, antialias: true, alpha: true
        });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.75));
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 1.1;
        renderer.setClearColor(0x000000, 0);

        var scene = new THREE.Scene();
        var env = makeEnvironment(THREE, renderer);
        scene.environment = env;

        var camera = new THREE.PerspectiveCamera(34, 1, 0.1, 20);
        camera.position.set(0, 0.45, 3.1);
        camera.lookAt(0, 0.05, 0);

        /* exactly 2 lights */
        var key = new THREE.DirectionalLight(0xfff1d6, 2.2);
        key.position.set(2.5, 3, 2.5);
        scene.add(key);
        var fill = new THREE.DirectionalLight(0xcfd8e6, 0.9);
        fill.position.set(-2.5, 1, -1.5);
        scene.add(fill);

        var material = new THREE.MeshPhysicalMaterial({
          color: new THREE.Color(init.color),
          metalness: init.metalness,
          roughness: init.roughness,
          clearcoat: 0.35,
          clearcoatRoughness: 0.25,
          envMapIntensity: 1.15
        });

        var faucet = buildFaucet(THREE, material);
        scene.add(faucet);

        /* sizing */
        function resize() {
          var w = canvas.clientWidth || 1;
          var h = canvas.clientHeight || 1;
          renderer.setSize(w, h, false);
          camera.aspect = w / h;
          camera.updateProjectionMatrix();
        }
        resize();
        var ro = null;
        if ('ResizeObserver' in window) {
          ro = new ResizeObserver(resize);
          ro.observe(canvas);
        } else {
          window.addEventListener('resize', resize);
        }

        /* pause when off-screen */
        var visible = true;
        var io = null;
        if ('IntersectionObserver' in window) {
          io = new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) { visible = entry.isIntersecting; });
          }, { rootMargin: '100px 0px' });
          io.observe(canvas);
        }

        /* idle rotation loop */
        var raf = 0;
        var last = performance.now();
        var disposed = false;
        function tick(now) {
          if (disposed) return;
          raf = requestAnimationFrame(tick);
          var dt = Math.min((now - last) / 1000, 0.05);
          last = now;
          if (!visible) return;
          faucet.rotation.y += dt * 0.35; /* slow idle spin */
          renderer.render(scene, camera);
        }
        raf = requestAnimationFrame(tick);

        var gsap = (window.BrassLuxe && window.BrassLuxe.gsap) || window.gsap || null;

        function setFinish(finish) {
          var target = new THREE.Color(finish.color);
          var rough = typeof finish.roughness === 'number' ? finish.roughness : material.roughness;
          var metal = typeof finish.metalness === 'number' ? finish.metalness : material.metalness;
          if (gsap) {
            gsap.to(material.color, { r: target.r, g: target.g, b: target.b, duration: 0.9, ease: 'power2.out' });
            gsap.to(material, { roughness: rough, metalness: metal, duration: 0.9, ease: 'power2.out' });
          } else {
            material.color.copy(target);
            material.roughness = rough;
            material.metalness = metal;
          }
        }

        function dispose() {
          if (disposed) return;
          disposed = true;
          cancelAnimationFrame(raf);
          if (ro) ro.disconnect(); else window.removeEventListener('resize', resize);
          if (io) io.disconnect();
          if (gsap) {
            gsap.killTweensOf(material);
            gsap.killTweensOf(material.color);
          }
          faucet.traverse(function (obj) {
            if (obj.geometry) obj.geometry.dispose();
          });
          material.dispose();
          env.dispose();
          renderer.dispose();
          if (renderer.forceContextLoss) renderer.forceContextLoss();
        }

        return { setFinish: setFinish, dispose: dispose };
      });
    }
  };
  window.dispatchEvent(new Event('brassluxe:finish-ready'));
})();
